import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useT } from "../context/locale";
import { useAuth } from "../context/auth";
import { useToast } from "../context/toast";
import {} from "../lib/format";
import { useRelative, useDateTime } from "../lib/use-relative";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  Panel,
  RowActions,
  Table,
  Td,
  Tr,
} from "../components/ui";
import {
  DateField,
  FieldGrid,
  NumberField,
  TextField,
  Toggle,
} from "../components/ui/form";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import type { Paged, ReferralCode } from "../types/api";

export function Referrals() {
  const dateTime = useDateTime();
  const t = useT();
  const relative = useRelative();
  const { can } = useAuth();
  const toast = useToast();
  const [params] = useSearchParams();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filters = useFilters({ q: params.get("q") ?? "", isActive: "" }, () =>
    setPage(1),
  );
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const codes = useLive<Paged<ReferralCode>>("referrals", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        q: filters.applied.q || undefined,
        isActive: filters.applied.isActive || undefined,
      });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ReferralCode | null>(null);
  const [deleting, setDeleting] = useState<ReferralCode | null>(null);

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Copied", `${code} is on your clipboard.`);
    } catch {
      toast.error(t("toast.copyFailed"), t("toast.copyManually"));
    }
  }

  return (
    <div className="space-y-5">
      <FilterBar
        loading={codes.loading}
        state={filters}
        filters={[
          { key: "q", label: t("col.code"), placeholder: t("referrals.searchCode") },
          {
            key: "isActive",
            label: t("common.state"),
            type: "select",
            options: [
              { value: "", label: t("referrals.anyState") },
              { value: "true", label: t("referrals.active") },
              { value: "false", label: t("referrals.disabled") },
            ],
          },
        ]}
      />

      <Panel
        eyebrow={t("nav.group.control")}
        title={t("nav.referrals")}
        bodyClassName=""
        actions={
          <>
            <Button size="sm" onClick={codes.reload}>
              {t("action.refresh")}
            </Button>
            {can("admin") ? (
              <Button
                size="sm"
                variant="gold"
                onClick={() => setCreating(true)}
              >
                {t("referrals.generate")}
              </Button>
            ) : null}
          </>
        }
      >
        {codes.error ? (
          <div className="p-5">
            <ErrorNote message={codes.error} onRetry={codes.reload} />
          </div>
        ) : (
          <>
            <Table
              minWidth="60rem"
              head={[
                <SortHeader key="c" field="code" state={sort}>
                  {t("col.code")}
                </SortHeader>,
                t("field.note"),
                <SortHeader key="u" field="useCount" state={sort} align="right">
                  {t("referrals.used")}
                </SortHeader>,
                "Signups",
                <SortHeader key="m" field="maxUses" state={sort} align="right">
                  {t("referrals.remaining")}
                </SortHeader>,
                <SortHeader key="e" field="expiresAt" state={sort}>
                  {t("col.expires")}
                </SortHeader>,
                t("common.state"),
                <SortHeader key="cr" field="createdAt" state={sort}>
                  {t("common.created")}
                </SortHeader>,
                <span key="a" className="block text-right">
                  {t("col.actions")}
                </span>,
              ]}
            >
              {codes.loading ? (
                <LoadingRows columns={9} />
              ) : codes.data && codes.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={9}>
                    <EmptyState
                      title={t("referrals.empty")}
                      detail={t("referrals.emptyDetail")}
                      action={
                        can("admin") ? (
                          <Button
                            variant="gold"
                            onClick={() => setCreating(true)}
                          >
                            {t("referrals.generate")}
                          </Button>
                        ) : undefined
                      }
                    />
                  </Td>
                </Tr>
              ) : (
                (codes.data?.items ?? []).map((code) => (
                  <Tr key={code.id}>
                    <Td>
                      <span className="money accent text-[14px] font-semibold">
                        {code.code}
                      </span>
                    </Td>
                    <Td
                      className="text-muted max-w-[14rem] truncate text-[12px]"
                      title={code.note ?? ""}
                    >
                      {code.note ?? "—"}
                    </Td>
                    <Td className="text-right">
                      <UsageMeter used={code.useCount} max={code.maxUses} />
                    </Td>
                    <Td className="money text-center">
                      {/* Diverges from "used" once a player is deleted, which is
                          worth seeing rather than quietly reconciling away. */}
                      <span title={t("referrals.usedBy")}>
                        {code.signupCount ?? 0}
                      </span>
                    </Td>
                    <Td className="money text-right">
                      {code.remainingUses ?? code.maxUses - code.useCount}
                    </Td>
                    <Td className="text-dim text-[12px]">
                      {code.expiresAt ? dateTime(code.expiresAt) : t("common.never")}
                    </Td>
                    <Td>
                      {code.usable ? (
                        <Badge tone="success">{t("referrals.usable")}</Badge>
                      ) : code.useCount >= code.maxUses ? (
                        <Badge tone="neutral">{t("referrals.usedUp")}</Badge>
                      ) : !code.isActive ? (
                        <Badge tone="danger">{t("referrals.disabled")}</Badge>
                      ) : (
                        <Badge tone="warning">{t("referrals.expired")}</Badge>
                      )}
                    </Td>
                    <Td className="text-dim text-[12px]">
                      {relative(code.createdAt)}
                    </Td>
                    <Td>
                      <RowActions>
                        <ActionButton
                          icon={ActionIcon.copy}
                          onClick={() => copy(code.code)}
                        >
                          {t("action.copy")}
                        </ActionButton>
                        {can("admin") ? (
                          <>
                            <ActionButton
                              icon={ActionIcon.edit}
                              onClick={() => setEditing(code)}
                            >
                              {t("action.edit")}
                            </ActionButton>
                            <ActionButton
                              icon={ActionIcon.delete}
                              tone="danger"
                              onClick={() => setDeleting(code)}
                            >
                              {t("action.delete")}
                            </ActionButton>
                          </>
                        ) : null}
                      </RowActions>
                    </Td>
                  </Tr>
                ))
              )}
            </Table>

            {codes.data ? (
              <PageBar
                page={codes.data.page}
                pages={codes.data.pages}
                total={codes.data.total}
                perPage={perPage}
                onPage={setPage}
                onPerPage={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>

      <CreateCodeDialog
        open={creating}
        onClose={() => setCreating(false)}
        onDone={(code) => {
          toast.success(t("toast.codeCreated"), `${code} is ready to hand out.`);
          codes.reload();
        }}
      />
      <EditCodeDialog
        code={editing}
        onClose={() => setEditing(null)}
        onDone={codes.reload}
      />
      <DeleteCodeDialog
        code={deleting}
        onClose={() => setDeleting(null)}
        onDone={codes.reload}
      />
    </div>
  );
}

/** Used-against-cap as a bar, so exhaustion is visible without reading numbers. */
function UsageMeter({ used, max }: { used: number; max: number }) {
  const ratio = max === 0 ? 0 : Math.min(1, used / max);
  const full = used >= max;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="money text-[12px]">
        {used}/{max}
      </span>
      <span
        className="h-1.5 w-14 overflow-hidden rounded-full"
        style={{ background: "var(--surface-inset)" }}
        aria-hidden
      >
        <span
          className="block h-full rounded-full"
          style={{
            width: `${ratio * 100}%`,
            background: full ? "var(--danger)" : "var(--accent)",
          }}
        />
      </span>
    </span>
  );
}

function CreateCodeDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (code: string) => void;
}) {
  const t = useT();
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [maxUses, setMaxUses] = useState("1");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uses = Number.parseInt(maxUses, 10);
  const valid =
    Number.isInteger(uses) &&
    uses > 0 &&
    (code === "" || code.trim().length >= 4);

  function close() {
    setCode("");
    setNote("");
    setMaxUses("1");
    setExpiresAt("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createReferralCode({
        code: code.trim() || undefined,
        note: note.trim() || undefined,
        maxUses: uses,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      onDone(created.code);
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.createCode"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      eyebrow={t("nav.group.control")}
      title={t("referrals.generateTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {t("common.generate")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
          A player cannot register without a code. Leave it blank and the server
          picks one that avoids characters people misread aloud.
        </p>

        <TextField
          label={t("col.code")}
          value={code}
          onChange={setCode}
          placeholder={t("referrals.codePlaceholder")}
          hint={t("referrals.codeHint")}
          error={
            code !== "" && code.trim().length < 4
              ? t("error.minCode")
              : null
          }
          maxLength={32}
          uppercase
          mono
        />
        <TextField
          label={t("field.note")}
          value={note}
          onChange={setNote}
          placeholder={t("referrals.notePlaceholder")}
          hint={t("referrals.noteHint")}
        />
        <FieldGrid>
          <NumberField
            label={t("referrals.maxUses")}
            value={maxUses}
            onChange={setMaxUses}
            required
          />
          <DateField
            label={t("col.expires")}
            value={expiresAt}
            onChange={setExpiresAt}
            hint={t("common.optional")}
          />
        </FieldGrid>

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function EditCodeDialog({
  code,
  onClose,
  onDone,
}: {
  code: ReferralCode | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [maxUses, setMaxUses] = useState("1");
  const [expiresAt, setExpiresAt] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    setNote(code.note ?? "");
    setMaxUses(String(code.maxUses));
    setExpiresAt(code.expiresAt ? code.expiresAt.slice(0, 10) : "");
    setIsActive(code.isActive);
    setError(null);
  }, [code]);

  const uses = Number.parseInt(maxUses, 10);
  const belowUsed = code ? uses < code.useCount : false;
  const valid = Number.isInteger(uses) && uses > 0 && !belowUsed;

  async function submit() {
    if (!code || !valid) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateReferralCode(code.id, {
        note: note.trim() || null,
        maxUses: uses,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        isActive,
      });
      toast.success(t("toast.codeUpdated"));
      onDone();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.updateCode"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(code)}
      onClose={onClose}
      eyebrow={t("nav.group.control")}
      title={`Edit ${code?.code ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {t("action.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div
          className="rounded-lg px-4 py-3"
          style={{ background: "var(--surface-inset)" }}
        >
          <p className="eyebrow mb-1">{t("col.usage")}</p>
          <p className="text-muted text-[13px]">
            {t("referrals.used")} <span className="money">{code?.useCount ?? 0}</span> of{" "}
            <span className="money">{code?.maxUses ?? 0}</span> ·{" "}
            <span className="money">{code?.signupCount ?? 0}</span> account
            {code?.signupCount === 1 ? "" : "s"} still exist against it.
          </p>
        </div>

        <TextField label={t("field.note")} value={note} onChange={setNote} />

        <FieldGrid>
          <NumberField
            label={t("referrals.maxUses")}
            value={maxUses}
            onChange={setMaxUses}
            error={belowUsed ? t("referrals.alreadyUsed", { count: code?.useCount ?? 0 }) : null}
            required
          />
          <DateField
            label={t("col.expires")}
            value={expiresAt}
            onChange={setExpiresAt}
            hint={t("referrals.expiresHint")}
          />
        </FieldGrid>

        {/* A yes/no is a switch, not a two-option dropdown. */}
        <Toggle
          label={t("referrals.active")}
          hint={
            isActive
              ? t("referrals.activeHint")
              : t("referrals.inactiveHint")
          }
          checked={isActive}
          onChange={setIsActive}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function DeleteCodeDialog({
  code,
  onClose,
  onDone,
}: {
  code: ReferralCode | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteReferralCode(code.id);
      toast.success(
        t("toast.codeDeleted"),
        `${result.code} can no longer be used to register.`,
      );
      onDone();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.deleteCode"),
      );
    } finally {
      setBusy(false);
    }
  }

  const signups = code?.signupCount ?? 0;

  return (
    <Modal
      open={Boolean(code)}
      onClose={onClose}
      eyebrow={t("nav.group.control")}
      title={`Delete ${code?.code ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button variant="danger" onClick={submit} loading={busy}>
            {t("referrals.deleteSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {signups > 0 ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{ background: "var(--warning-bg)", color: "var(--warning)" }}
          >
            {t("referrals.signupsNote", { count: signups })}
          </p>
        ) : null}

        <p className="text-muted text-[13px]">
          {t("referrals.disableExplain")}
        </p>

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}
